import { useRef, useEffect } from 'react';
import { Box } from '@nimbus-ds/components';
import { ChatAIMessage, ChatUserMessage, ChatLoading } from '..';
import { useAgentChatContext } from '../../contexts';

/**
 * ChatMessagesContainer handles the display of chat messages with automatic scrolling
 * and provides new conversation functionality.
 */
function ChatMessagesContainer(): JSX.Element {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { messages, isLoading } = useAgentChatContext();

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, isLoading]);

  return (
    <Box
      ref={messagesContainerRef}
      display="flex"
      flexDirection="column"
      flex="1"
      padding="4"
      gap="4"
      overflow="auto"
      role="log"
      aria-label="Chat messages"
      aria-live="polite"
    >
      {/* Render messages */}
      {messages.map((message) => {
        if (message.isUser) {
          return <ChatUserMessage key={message.id} message={message} />;
        }

        return <ChatAIMessage key={message.id} message={message} />;
      })}

      {/* Loading indicator */}
      {isLoading && <ChatLoading />}
    </Box>
  );
}

export default ChatMessagesContainer;
